import React, { useContext, useEffect, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { AppContext } from '../window';
import { Link, useNavigate, useLocation } from 'react-router';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DiamondIcon from '@mui/icons-material/Diamond';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockResetIcon from '@mui/icons-material/LockReset';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginModal from '../user/LoginModal';
import SignupModal from '../user/SignupModal';
import RequestResetModal from '../user/RequestResetModal';
import TermsConditionsModal from '../about/TermsConditionsModal';
import PrivacyPolicyModal from '../about/PrivacyPolicyModal';
import { NavigationContext } from './NavigationContext';
import Avatar from '../libs/AppAvatar';
import { useConversationContext } from '../context/ConversationContext';
import { NotificationContext } from '../context/NotificationContext';
import { useArchiveContext } from '../context/ArchiveContext';
import { performLogout } from '../utils/logout';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../i18n';

interface NavItemsProps {
  onSearchClick?: () => void;
  handleSearchClick: (e: React.MouseEvent) => void;
  unreadMessageCount?: number;
}
const NavItems: React.FC<NavItemsProps> = ({ onSearchClick, handleSearchClick, unreadMessageCount }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path: string) => {
    // For root, match exactly
    if (path === '/') {
      return location.pathname === '/';
    }
    // For others, match if starts with path
    return location.pathname.startsWith(path);
  };
  return (
    <>
      <Nav.Link as={Link} to="/search" className={`me-lg-4${isActive('/search') ? ' active' : ''}`} onClick={handleSearchClick}><SearchIcon fontSize="small" /> {t('general.search')}</Nav.Link>
      <Nav.Link as={Link} to="/inbox" className={`me-lg-4${isActive('/inbox') ? ' active' : ''}`}>
        <ChatBubbleOutlineIcon fontSize="small" /> {t('general.chats')}
        {unreadMessageCount && unreadMessageCount > 0 ? (
          <Badge bg="primary" pill className="ms-1">{unreadMessageCount}</Badge>
        ) : null}
      </Nav.Link>
      <Nav.Link as={Link} to="/match" className={`me-lg-4${isActive('/match') ? ' active' : ''}`}><FavoriteIcon fontSize="small" /> {t('general.match')}</Nav.Link>
    </>
  );
};

interface NavigationProps {
  landingPageMode?: boolean;
  loginText?: string;
  hideLogin?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ landingPageMode = false, loginText = 'Login', hideLogin = false }) => {
  const { t } = useTranslation();
  const { logo, user, setUser } = useContext(AppContext);
  const { hideFirstNavbar, hideSecondNavbar, onSearchClick } = useContext(NavigationContext);
  const { resetConversations } = useConversationContext();
  const { notifications, removeNotification } = useContext(NotificationContext);
  const { resetArchive } = useArchiveContext();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [ isPremium, setIsPremium ] = useState(false);
  const { react } = useContext(AppContext);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const avatarSrc = user?.displayPicture?.thumb || undefined;
  const logoSrc = typeof logo?.src === 'string' && logo.src.trim() !== '' ? logo.src : undefined;


  useEffect(() => {
    if(!user?.wallet?.premiumExpiry) {
      setIsPremium(false);
      return;
    }
    setIsPremium(new Date(user.wallet.premiumExpiry) > new Date());
  }, [user?.wallet?.premiumExpiry]);

  
  const navigate = useNavigate();

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate(user ? '/search' : '/');
    }
  };

  const handleShowLoginModal = () => setShowLoginModal(true);
  const handleCloseLoginModal = () => setShowLoginModal(false);
  const handleShowSignupModal = () => setShowSignupModal(true);
  const handleCloseSignupModal = () => setShowSignupModal(false);
  const handleShowPasswordResetModal = () => setShowPasswordResetModal(true);
  const handleClosePasswordResetModal = () => setShowPasswordResetModal(false);

  const handleLogout = async () => {
    await performLogout({
      navigate,
      setUser,
      resetConversations,
      resetArchive,
      notifications,
      removeNotification,
      reactConfig: react.config
    });
  };


  return (
    <>
      <Navbar 
        expand 
        className={`bg-body primary-nav primary-nav-transition pt-1 pb-1 pb-lg-2 ${hideFirstNavbar ? 'primary-nav--hidden' : ''}`}
        aria-hidden={hideFirstNavbar || undefined}
      >
        <Container>
          <Navbar.Brand
            onClick={handleSearchClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={logoSrc}
              loading="lazy"
              style={{ height: logo.height }}
              className="d-inline-block align-top"
              alt={t('general.logo_alt')}
            />
          </Navbar.Brand>

          {landingPageMode && !user ? (
            <Nav className="ms-auto">
              {!hideLogin && <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={handleShowLoginModal}><LoginIcon fontSize="small" /> {loginText}</Nav.Link>}
            </Nav>
          ) : user ? (
            <>
              <Nav className="d-none d-lg-flex">
                <NavItems onSearchClick={onSearchClick} handleSearchClick={handleSearchClick} unreadMessageCount={user.unreadMessageCount} />
              </Nav>
              <Nav>
                <Nav.Link as={Link} to="/credits" className={location.pathname === '/credits' ? 'active' : ''} style={{lineHeight: '38px'}}><CreditCardIcon fontSize="small" /><span className="d-none d-lg-inline-block ms-1">{t('general.credits')}</span> </Nav.Link>
                <NavDropdown
                  title={(
                    <div className="d-inline-flex align-items-center">
                      <div className="position-relative d-inline-block">
                        <Avatar
                          src={avatarSrc}
                          circle
                          size="md"
                          {...(isPremium ? { pulsingBorder: true, pulsingBorderColor: 'warning' } : {})}
                        />
                        {user.credentials?.emailStatus > 0 && (
                          <VerifiedIcon 
                            fontSize="medium"
                            className="position-absolute top-0 end-0 text-success bg-white rounded-circle p-1"
                            style={{ zIndex: 1000,marginRight: '-12px',marginTop: '-6px' }}
                          />
                        )}
                        {/* Bubble with chevron overlay bottom-right */}
                        <span className="nav-avatar-bubble" aria-hidden="true">
                          <KeyboardArrowDownIcon className="nav-avatar-bubble-icon" fontSize="inherit" />
                        </span>
                      </div>
                    </div>
                  )}
                  id="menu-dropdown"
                  align="end"
                  className="theme-dropdown"
                >
                  {/* User info section */}
                  {user.wallet?.premiumExpiry && new Date(user.wallet.premiumExpiry) > new Date() && (
                    <NavDropdown.Item disabled style={{ color: '#bfa100', fontWeight: 'bold', background: '#fffbe6' }}>
                      <DiamondIcon style={{ color: '#bfa100', marginRight: 4 }} /> {t('general.premium_member')}
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Item as={Link} to="/profile" style={location.pathname === '/profile' ? { color: 'var(--bs-primary)' } : {}}><AccountCircleIcon /> {t('general.profile')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/favorites" style={location.pathname === '/favorites' ? { color: 'var(--bs-primary)' } : {}}><StarIcon /> {t('general.my_favorites')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/settings" style={location.pathname === '/settings' ? { color: 'var(--bs-primary)' } : {}}><SettingsIcon /> {t('general.settings')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/credits" style={location.pathname === '/credits' ? { color: 'var(--bs-primary)' } : {}}><CreditCardIcon /> {t('general.buy_credits')}</NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoIcon /> {t('general.about_faq')}</NavDropdown.Item>
                  <NavDropdown.Divider />
                  <div className="px-3 py-2">
                    <Form.Check
                      type="switch"
                      id="theme-switch-member"
                      label={
                        <span className="d-flex align-items-center">
                          {theme === 'light' ? <LightModeIcon fontSize="small" className="me-2" /> : <DarkModeIcon fontSize="small" className="me-2" />}
                          {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                        </span>
                      }
                      checked={theme === 'dark'}
                      onChange={toggleTheme}
                      className="mb-0"
                    />
                  </div>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}><LogoutIcon /> {t('general.logout')}</NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </>
          ) : (
            <Nav className="ms-auto d-none d-lg-flex">
              <Nav.Link as={Link} to="#" className="btn btn-outline-primary me-2" onClick={handleShowSignupModal}><PersonAddIcon fontSize="small" /> {t('general.sign_up')}</Nav.Link>
              <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={handleShowLoginModal}><LoginIcon fontSize="small" /> {t('general.login')}</Nav.Link>
              <NavDropdown
                title={<MoreVertIcon />}
                id="guest-menu-dropdown"
                align="end"
                className="theme-dropdown"
              >
                <NavDropdown.Item onClick={handleShowPasswordResetModal}><LockResetIcon /> {t('general.reset_password')}</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoIcon /> {t('general.about_faq')}</NavDropdown.Item>
                <NavDropdown.Divider />
                <div className="px-3 py-2">
                  <Form.Check
                    type="switch"
                    id="theme-switch-guest"
                    label={
                      <span className="d-flex align-items-center">
                        {theme === 'light' ? <LightModeIcon fontSize="small" className="me-2" /> : <DarkModeIcon fontSize="small" className="me-2" />}
                        {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                      </span>
                    }
                    checked={theme === 'dark'}
                    onChange={toggleTheme}
                    className="mb-0"
                  />
                </div>
              </NavDropdown>
            </Nav>
          )}
        </Container>
  </Navbar>

      {!hideSecondNavbar && !landingPageMode && user && (
        <Navbar expand className="bg-body primary-nav d-lg-none pt-0">
          <Container className="d-flex justify-content-center">
            <Nav>
              <NavItems onSearchClick={onSearchClick} handleSearchClick={handleSearchClick} unreadMessageCount={user.unreadMessageCount} />
            </Nav>
          </Container>
        </Navbar>
      )}

      {!hideSecondNavbar && !landingPageMode && !user && (
        <Navbar expand className="bg-body primary-nav d-lg-none pt-0">
          <Container className="d-flex position-relative">
            <Nav className="w-100 d-flex justify-content-center align-items-center">
              <Nav.Link as={Link} to="#" className="btn btn-outline-primary me-2" onClick={handleShowSignupModal}><PersonAddIcon fontSize="small" /> {t('general.sign_up')}</Nav.Link>
              <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={handleShowLoginModal}><LoginIcon fontSize="small" /> {t('general.login')}</Nav.Link>
            </Nav>
            <NavDropdown
              title={<MoreVertIcon />}
              id="guest-menu-dropdown-mobile"
              align="end"
              className="position-absolute end-0 pe-3 theme-dropdown"
            >
              <NavDropdown.Item onClick={handleShowPasswordResetModal}><LockResetIcon /> {t('general.reset_password')}</NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoIcon /> {t('general.about')}</NavDropdown.Item>
              <NavDropdown.Divider />
              <div className="px-3 py-2">
                <Form.Check
                  type="switch"
                  id="theme-switch-guest-mobile"
                  label={
                    <span className="d-flex align-items-center">
                      {theme === 'light' ? <LightModeIcon fontSize="small" className="me-2" /> : <DarkModeIcon fontSize="small" className="me-2" />}
                      {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                    </span>
                  }
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                  className="mb-0"
                />
              </div>
            </NavDropdown>
          </Container>
        </Navbar>
      )}

      <LoginModal 
        show={showLoginModal} 
        onHide={handleCloseLoginModal} 
        onShowSignup={handleShowSignupModal}
        onShowPasswordReset={handleShowPasswordResetModal}
      />
      <SignupModal 
        show={showSignupModal} 
        onHide={handleCloseSignupModal} 
      />
      <RequestResetModal
        show={showPasswordResetModal}
        onHide={handleClosePasswordResetModal}
        onShowLogin={handleShowLoginModal}
      />
      <TermsConditionsModal 
        show={showTermsModal} 
        onHide={() => setShowTermsModal(false)} 
      />
      <PrivacyPolicyModal 
        show={showPrivacyModal} 
        onHide={() => setShowPrivacyModal(false)} 
      />
    </>
  );
};

export default Navigation;
